"use client";

import React from "react";
import { ArrowLeft, ArrowRight, Calendar, Clock, Share2, Tag, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: {
  getCurrentWPQueryParams: () => Record<string, any>;
};

function SinglePostContent() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl animate-pulse space-y-6">
        <div className="h-4 w-32 rounded-md bg-muted" />
        <div className="h-12 w-full rounded-md bg-muted" />
        <div className="flex items-center gap-4">
          <div className="size-12 rounded-full bg-muted" />
          <div className="space-y-2">
            <div className="h-4 w-40 rounded-md bg-muted" />
            <div className="h-3 w-24 rounded-md bg-muted" />
          </div>
        </div>
        <div className="aspect-video w-full rounded-xl bg-muted" />
        <div className="space-y-3 pt-6">
          <div className="h-4 w-full rounded-md bg-muted" />
          <div className="h-4 w-full rounded-md bg-muted" />
          <div className="h-4 w-5/6 rounded-md bg-muted" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl rounded-xl border border-destructive/30 bg-destructive/10 p-6 text-center text-foreground">
        <p className="font-medium text-destructive">Unable to load article details.</p>
        <div className="mt-4">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const post = posts[0];

  if (!post) {
    return (
      <div className="mx-auto max-w-4xl rounded-xl border border-border bg-card p-10 text-center text-card-foreground shadow-sm">
        <h2 className="font-serif text-2xl font-bold">Article Not Found</h2>
        <p className="mt-2 text-sm text-muted-foreground">The requested energy advisory article could not be retrieved.</p>
        <div className="mt-6">
          <Link to="/HomePage">
            <Button variant="default">Return to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const authorName = post.author?.name || "Energy Consultant";
  const authorAvatar = post.author?.avatar?.["96"] || post.author?.avatar?.["48"] || "";
  const publishDate = post.date ? new Date(post.date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "Recent Update";
  const categoryName = post.categories && post.categories.length > 0 ? post.categories[0].name : "Energy Advice";

  return (
    <article className="mx-auto max-w-4xl">
      {/* Top Breadcrumb / Category */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/60 px-3.5 py-1 text-xs font-medium text-foreground">
          <Tag className="size-3.5 text-chart-2" />
          <span>{categoryName}</span>
        </div>
        <Link to="/HomePage">
          <Button variant="ghost" size="sm" className="gap-1.5 text-xs text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-3.5" />
            Back to Home
          </Button>
        </Link>
      </div>

      {/* Main Post Title */}
      <h1 className="mt-6 font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:leading-[1.15]">
        {post.title || "Energy Management Insights"}
      </h1>

      {/* Author & Meta Bar */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-y border-border py-4">
        <div className="flex items-center gap-3">
          <Avatar className="size-12 border border-border shadow-xs">
            {authorAvatar ? (
              <AvatarImage src={authorAvatar} alt={authorName} />
            ) : null}
            <AvatarFallback className="bg-primary text-primary-foreground font-semibold">
              {authorName.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="text-sm font-semibold text-foreground">{authorName}</div>
            <div className="text-xs text-muted-foreground">Energy Advisory Expert</div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Calendar className="size-3.5 text-chart-2" />
            <span>{publishDate}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="size-3.5 text-chart-2" />
            <span>5 min read</span>
          </div>
        </div>
      </div>

      {/* Featured Image */}
      {post.featuredImage ? (
        <div className="mt-8 overflow-hidden rounded-xl border border-border bg-muted shadow-sm">
          <img
            src={post.featuredImage}
            alt={post.title || "Article feature"}
            loading="lazy"
            decoding="async"
            className="h-auto w-full max-h-[480px] object-cover"
          />
        </div>
      ) : (
        <div className="mt-8 overflow-hidden rounded-xl border border-border bg-muted shadow-sm">
          <img
            src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1783268/img/energy_advisory_featured.png"
            alt="Modern solar panels on commercial building"
            loading="lazy"
            decoding="async"
            className="h-auto w-full max-h-[480px] object-cover"
          />
        </div>
      )}

      {/* Main Content Area */}
      <div className="prose prose-neutral max-w-none pt-8 text-foreground">
        {post.content ? (
          <div
            className="space-y-6 text-base leading-relaxed text-foreground/90 [&>h2]:font-serif [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:text-foreground [&>h3]:font-serif [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:text-foreground [&>p]:leading-relaxed [&>ul]:list-disc [&>ul]:pl-6 [&>blockquote]:border-l-2 [&>blockquote]:border-chart-2 [&>blockquote]:pl-4 [&>blockquote]:italic"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        ) : (
          <div className="space-y-6 text-base leading-relaxed text-foreground/90">
            <p>
              Managing energy costs effectively requires a strategic approach to cost management and efficiency. At Cost Management Ireland, we help businesses navigate the complexities of the Irish energy market to secure better contract terms and reduce overall consumption.
            </p>
            <h2 className="font-serif text-2xl font-bold text-foreground">
              Strategic Energy Cost Management and Efficiency
            </h2>
            <p>
              From electricity cost management to SEAI-supported energy audits, our team provides the clarity needed to make informed decisions. We assist in reviewing your current usage, identifying waste, and implementing practical solutions that lead to long-term savings.
            </p>
            <blockquote className="border-l-2 border-chart-2 pl-4 italic text-muted-foreground">
              {"Spend less, Save more through independent energy advice."}
            </blockquote>
            <p>
              Whether you are looking to renew your contract or apply for an SEAI Energy Audit Voucher, our experts are here to guide you through every step of the process, ensuring your business remains energy-efficient and cost-effective.
            </p>
          </div>
        )}
      </div>

      {/* Post Author Box */}
      <div className="mt-12 rounded-xl border border-border bg-card p-6 text-card-foreground shadow-sm md:p-8">
        <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center">
          <Avatar className="size-16 border-2 border-border shadow-xs">
            {authorAvatar ? (
              <AvatarImage src={authorAvatar} alt={authorName} />
            ) : null}
            <AvatarFallback className="bg-primary text-primary-foreground text-xl font-bold">
              {authorName.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="font-serif text-lg font-bold text-foreground">{authorName}</h3>
                <p className="text-xs text-muted-foreground">Energy Advisory Expert at Cost Management Ireland</p>
              </div>
              <Link to="/AboutUsPage">
                <Button variant="outline" size="sm" className="text-xs">
                  View Profile
                </Button>
              </Link>
            </div>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Dedicated to helping Irish businesses optimize their energy spend through expert cost management, grant support, and practical efficiency advice.
            </p>
          </div>
        </div>
      </div>
    </article>
  );
}

function RelatedPostsList() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} data-index={i} className="h-72 animate-pulse rounded-xl bg-muted" />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-center text-sm text-muted-foreground">
        No related articles available at this time.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {posts.map((item, i) => {
        const itemCategory = item.categories && item.categories.length > 0 ? item.categories[0].name : "Energy Advice";
        const itemDate = item.date ? new Date(item.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Recent";
        
        return (
          <div key={item.id} data-index={i} className="flex">
            <Card className="flex w-full flex-col justify-between overflow-hidden border border-border bg-card shadow-xs transition-shadow hover:shadow-md">
              <div>
                {item.featuredImage ? (
                  <div className="aspect-video w-full overflow-hidden bg-muted">
                    <img
                      src={item.featuredImage}
                      alt={item.title || "Related article"}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  </div>
                ) : (
                  <div className="aspect-video w-full overflow-hidden bg-muted">
                    <img
                      src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1783268/img/related_energy_article.png"
                      alt="Professional energy audit in progress"
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  </div>
                )}
                <CardHeader className="space-y-2 pt-5">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="font-medium text-chart-2">{itemCategory}</span>
                    <span>{itemDate}</span>
                  </div>
                  <CardTitle className="font-serif text-lg font-bold leading-snug line-clamp-2 text-foreground">
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs leading-relaxed text-muted-foreground line-clamp-2">
                    {item.excerpt ? item.excerpt.replace(/<[^>]+>/g, "") : "Practical energy advice and market updates from Cost Management Ireland."}
                  </p>
                </CardContent>
              </div>
              <CardFooter className="pt-2">
                <Link to={item.link || "#"} className="w-full">
                  <Button variant="outline" size="sm" className="w-full justify-between text-xs">
                    <span>Read Article</span>
                    <ArrowRight className="size-3.5" />
                  </Button>
                </Link>
              </CardFooter>
            </Card>
          </div>
        );
      })}
    </div>
  );
}

export default function Article() {
  const singlePostQueryParams = typeof window !== "undefined" && typeof wvcClient !== "undefined" && wvcClient.getCurrentWPQueryParams
    ? wvcClient.getCurrentWPQueryParams()
    : { post_type: "post", per_page: 1 };

  const singlePostQuery = new WP_Query(singlePostQueryParams);
  const relatedPostsQuery = new WP_Query({ post_type: "post", per_page: 3 });

  return (
    <section data-section-id="150654"
      id="single-post-template-section"
      data-nav="light"
      className="bg-background py-16 text-foreground sm:py-20 lg:py-24"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Single Post Details */}
        <PostsProvider wp_query={singlePostQuery}>
          <SinglePostContent />
        </PostsProvider>

        {/* Separator */}
        <div className="mx-auto my-16 max-w-4xl sm:my-20">
          <Separator className="bg-border" />
        </div>

        {/* Related Posts Section */}
        <div className="mx-auto max-w-5xl">
          <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <span className="text-xs font-semibold tracking-wider text-chart-2 uppercase">
                Further Reading
              </span>
              <h2 className="mt-1 font-serif text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                Related Energy Insights
              </h2>
            </div>
            <Link to="/MarketUpdatesPage">
              <Button variant="outline" size="sm" className="gap-1.5 text-xs">
                <span>View All Updates</span>
                <ArrowRight className="size-3.5" />
              </Button>
            </Link>
          </div>

          <PostsProvider wp_query={{ post_type: "post", per_page: 3, status: ["publish"] }}>
            <RelatedPostsList />
          </PostsProvider>
        </div>
      </div>
    </section>
  );
}